import { NOT_SAVED_ID } from "app/model"
import { getPercent } from "app/tools/common"
import { CampaignAPI, ForecastAPI, SspPublisherAPI } from "generated-api"
import { checkIfSessionIsOrWillExpired } from "user/tools/common"
import { IOperator, IOperatorDTO, IOperatorRepository, IOperatorsForecast } from "./model"

export const operatorRPO: IOperatorRepository = {
  getAllOperators(sspIds: number[], nameFilter?: string): Promise<IOperator[]> {
    return new Promise<IOperator[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        SspPublisherAPI.getApiSspPublisherList(
          undefined,
          undefined,
          nameFilter,
          nameFilter ? "contains" : undefined,
          undefined,
          undefined,
          undefined,
          sspIds
        )
          .then(response => resolve(response.map(o => getOperatorFromRaw(o))))
          .catch((error: any) => {
            console.error("Server error while getting all operators", error)
            reject("operator.get.all.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting all operators", ex)
        reject("operator.get.all.error.unexpected")
      }
    })
  },
  getOperatorsById(operatorIds: string[]): Promise<IOperator[]> {
    // operatorIds = publisherNames
    return new Promise<IOperator[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        SspPublisherAPI.getApiSspPublisherList(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          operatorIds
        )
          .then(response => resolve(response.map(o => getOperatorFromRaw(o))))
          .catch((error: any) => {
            console.error("Server error while getting operators by id", error)
            reject("operator.get.by.ids.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting operators by ids", ex)
        reject("operator.get.by.ids.error.unexpected")
      }
    })
  },
  getCampaignOperators(campaignId: number): Promise<IOperator[]> {
    return new Promise<IOperator[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignSspPublisherList(campaignId)
          .then(response =>
            resolve(
              response.map(operatorAssociation => ({
                ...getOperatorFromRaw(operatorAssociation.ssp_publisher as IOperatorDTO),
                id: operatorAssociation.id
              }))
            )
          )
          .catch((error: any) => {
            console.error("Server error while getting all operator associations", error)
            reject("operator.get.campaign.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting all operator associations", ex)
        reject("operator.get.campaign.error.unexpected")
      }
    })
  },
  associateOperator(campaignId: number, operator: IOperator): Promise<IOperator> {
    return new Promise<IOperator>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignSspPublisher({
          campaign_id: campaignId,
          ssp_publisher_id: operator.publisher_id
        })
          .then(response =>
            resolve({
              ...operator,
              id: response.id
            })
          )
          .catch((error: any) => {
            console.error("Server error while associating operator", error)
            reject("operator.associate.error.server")
          })
      } catch (ex) {
        console.error("Server error while associating operator", ex)
        reject("operator.associate.error.unexpected")
      }
    })
  },
  setOperators(campaignId: number, operators: IOperator[]): Promise<IOperator[]> {
    return new Promise<IOperator[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignSspPublisherList(
          campaignId,
          operators.map(o => ({ campaign_id: campaignId, ssp_publisher_id: o.publisher_id }))
        )
          .then(response => {
            const rawOperatorAssociations = response
            // ex of rawOperatorAssociation:
            // {
            //   campaign_id: 2239
            //   id: 303
            //   ssp_publisher_id: 1
            // }
            const newOperators: IOperator[] = []
            const operatorsById: { [operatorId: number]: IOperator } = {}
            for (const operator of operators) {
              operatorsById[operator.publisher_id] = operator
            }
            for (const rawOperatorAssociation of rawOperatorAssociations) {
              const operator = operatorsById[rawOperatorAssociation.ssp_publisher_id]
              if (!operator) {
                console.error(`Unable to find operator id="${rawOperatorAssociation.ssp_publisher_id}"`)
                continue
              }
              newOperators.push({
                ...operator,
                id: rawOperatorAssociation.id
              })
            }
            resolve(newOperators)
          })
          .catch((error: any) => {
            console.error("Server error while settings campaign operators", error)
            reject("operator.set.error.server")
          })
      } catch (ex) {
        console.error("Server error while settings campaign operators", ex)
        reject("operator.set.error.unexpected")
      }
    })
  },
  unassociateOperator(campaignId: number, associationId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.deleteApiCampaignSspPublisher(campaignId, associationId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while unassociating operator", error)
            reject("operator.unassociate.error.server")
          })
      } catch (ex) {
        console.error("Server error while unassociating operator", ex)
        reject("operator.unassociate.error.unexpected")
      }
    })
  },
  getOperatorForecast(campaignId: number): Promise<IOperatorsForecast> {
    return new Promise<IOperatorsForecast>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        ForecastAPI.getApiForecastDoohPublisherSsp(campaignId)
          .then(response => {
            const forecast: IOperatorsForecast = {}
            if (response.groups) {
              response.groups.map((g: any) => {
                forecast[g.publisher_id] = {
                  screen: g.screen_count,
                  screenTotal: response.total_screens,
                  screenPercent: getPercent(g.screen_count, response.total_screens),
                  impression: g.impressions,
                  impressionTotal: response.total_impressions,
                  impressionPercent: getPercent(g.impressions, response.total_impressions),
                  spendInCad: g.worth_cad
                }
              })
            }
            resolve(forecast)
          })
          .catch((error: any) => {
            console.error("Server error while unassociating operator", error)
            reject("operator.unassociate.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting operators forecast", ex)
        reject("operator.get.forecast.error.unexpected")
      }
    })
  }
}

function getOperatorFromRaw(operatorDTO: IOperatorDTO): IOperator {
  return {
    id: NOT_SAVED_ID,
    publisher_id: operatorDTO.id,
    ssp_id: operatorDTO.ssp_id,
    publisher_name: operatorDTO.publisher_name,
    publisher_url: operatorDTO.publisher_url,
    default_deal_id: operatorDTO.default_deal_id,
    is_deleted: operatorDTO.is_deleted
  }
}

/* Ex of forecast
  "total_screens": 198175,
  "total_impressions": 141882633650,
  "total_spend": 1334185866.4700255,
  "groups": [
    {
      "screen_count": 606,
      "impressions": 330386840,
      "worth_cad": 4250687.369399999,
      "publisher_id": "Trooh",
      "ssp_id": 29
    }
  ]
*/
